import React, { useMemo } from "react";

import Decimal from "decimal.js";

import {
  Order,
  Proposal,
} from "@fartherfinance/frontend/api/Investment/hooks/useGetProposal";

import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import FullDataTable from "@src/sharedComponents/Table/FullDataTable";

interface Props {
  proposal: Proposal | undefined;
}

const TradeTotals: React.FC<Props> = (props) => {
  const totalTrades = useMemo(() => {
    if (props.proposal === undefined) {
      return undefined;
    }

    const trades = props.proposal.trades;

    const buys = trades.filter((t) => t.side === "Buy");
    const sells = trades.filter((t) => t.side === "Sell");

    function reduceTrades(trades: Order[]) {
      return trades.reduce(
        (acc, trade) => {
          const tradeSum = trade.quantity.mul(trade.price);

          return {
            securities: acc.securities.add(trade.securityId),
            totalCount: acc.totalCount.plus(1),
            quantity: acc.quantity.plus(trade.quantity),
            sum: acc.sum.plus(tradeSum),
          };
        },
        {
          securities: new Set(),
          totalCount: new Decimal(0),
          quantity: new Decimal(0),
          sum: new Decimal(0),
        }
      );
    }

    const buyInfo = reduceTrades(buys);
    const sellInfo = reduceTrades(sells);
    const total = {
      securities: new Set([...buyInfo.securities, ...sellInfo.securities]),
      totalCount: buyInfo.totalCount.plus(sellInfo.totalCount),
      quantity: buyInfo.quantity.plus(sellInfo.quantity),
      sum: buyInfo.sum.minus(sellInfo.sum),
    };

    return { buy: buyInfo, sell: sellInfo, total: total };
  }, [props.proposal]);

  return (
    <>
      {totalTrades === undefined ? (
        <FullDataTable
          key={"trade-totals-loading"}
          useQueryParams={false}
          columns={[
            "Type",
            "Diff Tickers",
            "Side Count",
            "Quantity",
            "Sum (Quantity x Price)",
          ]}
          defaultSortColumn={["Type", "asc"]}
          rows={Array.from({ length: 3 }).map((_, idx) => ({
            key: idx.toString(),
            Type: idx === 0 ? "Net" : idx === 1 ? "Total Buys" : "Total Sells",
            "Diff Tickers": { value: <Skeleton width={100} />, fullValue: "" },
            "Side Count": { value: <Skeleton width={100} />, fullValue: "" },
            Quantity: { value: <Skeleton width={100} />, fullValue: "" },
            "Sum (Quantity x Price)": {
              value: <Skeleton width={100} />,
              fullValue: "",
            },
          }))}
          emptyCell={<Skeleton />}
          hidePagination
          defaultRowsPerPage={3}
        />
      ) : (
        <FullDataTable
          key={"trade-totals-loaded"}
          useQueryParams={false}
          columns={[
            "Type",
            "Diff Tickers",
            "Side Count",
            "Quantity",
            "Sum (Quantity x Price)",
          ]}
          defaultSortColumn={["Type", "asc"]}
          rows={[
            {
              key: "Total Buys",
              Type: "Total Buys",
              "Diff Tickers":
                totalTrades.buy.securities.size.toLocaleString("en-US"),
              "Side Count": totalTrades.buy.totalCount
                .toNumber()
                .toLocaleString("en-US"),
              Quantity: totalTrades.buy.quantity
                .toNumber()
                .toLocaleString("en-US"),
              "Sum (Quantity x Price)": totalTrades.buy.sum
                .toNumber()
                .toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }),
            },
            {
              key: "Total Sells",
              Type: "Total Sells",
              "Diff Tickers":
                totalTrades.sell.securities.size.toLocaleString("en-US"),
              "Side Count": totalTrades.sell.totalCount
                .toNumber()
                .toLocaleString("en-US"),
              Quantity: totalTrades.sell.quantity
                .toNumber()
                .toLocaleString("en-US"),
              "Sum (Quantity x Price)": totalTrades.sell.sum
                .toNumber()
                .toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }),
            },
            {
              key: "Net",
              Type: "Net",
              "Diff Tickers":
                totalTrades.total.securities.size.toLocaleString("en-US"),
              "Side Count": totalTrades.total.totalCount
                .toNumber()
                .toLocaleString("en-US"),
              Quantity: totalTrades.total.quantity
                .toNumber()
                .toLocaleString("en-US"),
              "Sum (Quantity x Price)": totalTrades.total.sum
                .toNumber()
                .toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }),
            },
          ]}
          emptyCell={<Skeleton />}
          hidePagination
          defaultRowsPerPage={3}
        />
      )}
    </>
  );
};

export default TradeTotals;
